import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Collapse,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/system";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MapsInsightDefinition from "./MapsInsightDefinitionInfo";
import DuplicateMapModal from "../DuplicateMap/DuplicateMapModal";
import MapsInsightExpressionModal from "./MapsInsightExpressionModal";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { MapsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/mapsEndpointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import {
  setFieldResource,
  setOriginalParams,
  setPage,
  setRowsPerPage,
  setSort,
} from "../../../../../features/mapsSlice";

const expressionTableHeadings = {
  brand_id_name: "Brand ID & Name",
  report_name: "Report Name",
  rerport_id: "Report ID",
  raw_table: "Raw Table",
  config_name: "Config Name",
};

const MapsInsight = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [mapDetailsLoading, setMapDetailsLoading] = useState(false);
  const dispatch = useDispatch();
  const [openRows, setOpenRows] = useState<Record<number, boolean>>({});
  const [openModal, setOpenModal] = useState(false);
  const [openExpressionModal, setOpenExpressionModal] = useState(false);
  const mapId = searchParams.get("mapId");
  const page = useSelector((state: any) => state.maps.page);
  const rowsPerPage = useSelector((state: any) => state.maps.rowsPerPage);
  const sort = useSelector((state: any) => state.maps.sort);
  const mapDetails = useSelector((state: any) => state.maps.mapDetails);
  const { interfaceData, destinationTablesData } = useSelector(
    (state: any) => state.maps,
  );
  const interfaceName = React.useMemo(() => {
    if (!mapDetails?.backend_interface_id || !interfaceData?.length) {
      return "N/A";
    }

    const matchedInterface = interfaceData.find(
      (item: any) => item.id === mapDetails.backend_interface_id,
    );

    return matchedInterface?.affiliate_system_interface_name || "N/A";
  }, [mapDetails?.backend_interface_id, interfaceData]);

  const destinationTableName = React.useMemo(() => {
    if (!mapDetails?.destination_table_id || !destinationTablesData?.length) {
      return "N/A";
    }

    const matchedTable = destinationTablesData.find(
      (item: any) => item.id === mapDetails.destination_table_id,
    );

    return matchedTable?.destination_table || "N/A";
  }, [mapDetails?.destination_table_id, destinationTablesData]);

  const navigate = useNavigate();
  const handleChangePage = (_: any, newPage: number) => {
    dispatch(setPage(newPage));
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", newPage.toString()); // 0-indexed
    updatedParams.set("rowsPerPage", rowsPerPage.toString());
    setSearchParams(updatedParams);
  };

  const handleEditMaps = (mapId: string) => {
    const currentParams = searchParams.toString();
    dispatch(setOriginalParams(currentParams));
    navigate(`/data_collection/unifier_maps/edit_map/?mapId=${mapId}`);
  };

  const handleDuplicateMap = (mapId: string, mapName: string) => {
    const currentParams = searchParams.toString();
    dispatch(setOriginalParams(currentParams));

    // Encode mapName to make it URL safe
    const encodedName = encodeURIComponent(mapName);

    navigate(
      `/data_collection/unifier_maps/duplicate_map/?mapId=${mapId}&mapName=${encodedName}`,
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = +event.target.value;
    dispatch(setRowsPerPage(newRowsPerPage));
    dispatch(setPage(0));
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", "0"); // Reset page to 0
    updatedParams.set("rowsPerPage", newRowsPerPage.toString());
    setSearchParams(updatedParams);
  };

  useEffect(() => {
    if (mapId && oauthTokenHeader) {
      const fetchMapDetails = async () => {
        setMapDetailsLoading(true);
        try {
          await MapsEndpointLogic.getMapDetails(oauthTokenHeader, mapId);
          await MapsEndpointLogic.getInterface(oauthTokenHeader);
          await MapsEndpointLogic.getDestinationTables(oauthTokenHeader);
        } catch (error: any) {
          dispatch(
            setDisplayErrorSnackBar({
              message:
                error?.parsedText ||
                "Error while fetching data. Please try again later.",
              type: "error",
            }),
          );
        } finally {
          setMapDetailsLoading(false);
        }
      };
      fetchMapDetails();
    }
  }, [oauthTokenHeader, mapId, dispatch]);

  // useEffect(() => {
  //   return () => {
  //     // Remove mapId from URL when component unmounts
  //     const updatedParams = new URLSearchParams(searchParams);
  //     if (updatedParams.has("mapId")) {
  //       updatedParams.delete("mapId");
  //       setSearchParams(updatedParams);
  //     }
  //   };
  // }, [searchParams, setSearchParams]);

  const handleToggle = (index: number) => {
    setOpenRows((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const tableHeaders = [
    { id: 0, KEY: "field_mapping_id", label: "ID", sortable: true },
    {
      id: 1,
      KEY: "destination_field_name",
      label: "Destination Field Name",
      sortable: true,
    },
    {
      id: 2,
      KEY: "source_field_expression",
      label: "Source Field Expression",
      sortable: true,
    },
    {
      id: 3,
      KEY: "uni_field_calculation_type_name",
      label: "Calculation Type",
      sortable: true,
    },
    {
      id: 4,
      KEY: "raw_data_unique_definition_id",
      label: "Unique Definition",
      sortable: true,
    },
    { id: 5, KEY: "report_name", label: "Report Name", sortable: true },
    { id: 6, KEY: "more_info", label: "More Info", sortable: false },
  ];

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(
      setSort({
        keytoSort: header.KEY,
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      }),
    );
  };

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        const aValue = a[keytoSort];
        const bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        // Default sorting
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort],
  );

  const sortedRows = getSortedData(mapDetails?.field_mapping || []);
  const paginatedDetails = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const showExpressionDialog = async (row: any) => {
    const params = new URLSearchParams(searchParams);
    params.set("field_id", String(row?.field_mapping_id ?? ""));
    // encodeURIComponent to ensure URL-safe; but URLSearchParams will handle encoding too
    params.set(
      "source_field_expression",
      row?.source_field_expression ? String(row.source_field_expression) : "",
    );
    setSearchParams(params);

    // Make API call first, then open modal
    setLoading(true);
    try {
      await MapsEndpointLogic.getFieldResource(
        oauthTokenHeader,
        row.field_mapping_id,
      );
      setOpenExpressionModal(true);
    } catch (error: any) {
      console.error("Error fetching field resource:", error);
    } finally {
      setLoading(false);
    }
  };

  const expressionHandleClose = () => {
    dispatch(setFieldResource([]));
    setOpenExpressionModal(false);
    const params = new URLSearchParams(searchParams);
    params.delete("field_id");
    params.delete("source_field_expression");
    setSearchParams(params);
  };

  return (
    <>
      {loading && <SimpleBackdrop />}
      {mapDetailsLoading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Paper sx={{ mt: 2 }}>
            <Box sx={{ padding: "8px 16px" }}>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Grid size={8}>
                  <Typography variant="h3" sx={{ fontSize: "24px" }}>
                    {mapDetails?.map_name || "N/A"}
                  </Typography>
                  <span style={{ fontSize: "14px" }}>
                    <strong style={{ fontWeight: "500" }}> Map ID:</strong>{" "}
                    {mapDetails?.map_id || "N/A"}
                  </span>
                  <span style={{ fontSize: "14px", marginLeft: "14px" }}>
                    <strong style={{ fontWeight: "500" }}> Interface:</strong>{" "}
                    {interfaceName || "N/A"}
                  </span>
                  <span style={{ fontSize: "14px", marginLeft: "14px" }}>
                    <strong style={{ fontWeight: "500" }}>
                      {" "}
                      Destination Table:
                    </strong>{" "}
                    {destinationTableName || "N/A"}
                  </span>
                </Grid>
                <Grid
                  size={4}
                  container
                  direction="row"
                  spacing={0}
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Tooltip title="Edit" placement="top">
                    <IconButton
                      sx={{ width: "40px", height: "40px" }}
                      onClick={() => handleEditMaps(mapDetails?.map_id)}
                    >
                      <EditIcon color="secondary" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplicate" placement="top">
                    <IconButton
                      sx={{ width: "40px", height: "40px" }}
                      onClick={() =>
                        handleDuplicateMap(
                          mapDetails?.map_id,
                          mapDetails?.map_name,
                        )
                      }
                    >
                      <ContentCopyIcon fontSize="small" color="secondary" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
            <TableContainer className="mapsTable" component={Paper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header: any) => (
                      <TableCell key={header.id} align="left">
                        <TableSortLabel
                          active={sort.keytoSort === header.KEY}
                          direction={
                            sort.keytoSort === header.KEY
                              ? sort.direction
                              : "asc"
                          }
                          onClick={() => handleHeaderClick(header)}
                          disabled={!header.sortable}
                        >
                          {header.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedDetails && paginatedDetails?.length > 0 ? (
                    paginatedDetails?.map((row: any, rowIndex: number) => (
                      <React.Fragment key={row.field_mapping_id}>
                        <TableRow
                          sx={{
                            "& > *": {
                              borderBottom:
                                "1px solid rgba(224, 224, 224, 1) !important",
                            },
                          }}
                        >
                          <TableCell align="left">
                            <Box display="flex" alignItems="center">
                              {row.field_mapping_id || "N/A"}
                            </Box>
                          </TableCell>
                          <TableCell align="left">
                            {row.destination_field_name || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            <Typography
                              color="secondary"
                              sx={{
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                              onClick={() => showExpressionDialog(row)}
                            >
                              {row.source_field_expression || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            {row.uni_field_calculation_type_name || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            {row.raw_data_unique_definition_id || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            {row.report_name || "N/A"}
                          </TableCell>
                          <TableCell align="left">
                            <Box sx={{ textAlign: "center" }}>
                              <IconButton
                                aria-label="expand row"
                                size="small"
                                color="secondary"
                                onClick={() => handleToggle(rowIndex)}
                              >
                                {openRows[rowIndex] ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            style={{ padding: 0, border: "none" }}
                            colSpan={6}
                          >
                            <Collapse
                              in={openRows[rowIndex]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <MapsInsightDefinition rowData={row} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      <TableCell colSpan={6} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Stack
                spacing={1}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <TablePagination
                  rowsPerPageOptions={[]}
                  component="div"
                  count={mapDetails?.field_mapping?.length || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                      {
                        display: "none",
                      },
                    ".MuiTablePagination-actions": {
                      display: "none",
                    },
                    ".MuiTablePagination-toolbar": {
                      paddingLeft: "16px",
                    },
                  }}
                />
                <Pagination
                  count={Math.ceil(
                    (mapDetails?.field_mapping?.length || 0) / rowsPerPage,
                  )}
                  page={page + 1}
                  showFirstButton
                  showLastButton
                  className="pagination-button"
                  color="secondary"
                  size="small"
                  sx={{
                    ".css-wjh20t-MuiPagination-ul": {
                      justifyContent: "center",
                    },
                  }}
                  onChange={(_, value) => handleChangePage(null, value - 1)}
                />
                <TablePagination
                  rowsPerPageOptions={[10, 25, 100]}
                  component="div"
                  count={mapDetails?.field_mapping?.length || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  classes={{
                    displayedRows: "hidden-displayed-rows", // Apply the custom class
                  }}
                  sx={{
                    ".MuiTablePagination-actions": {
                      display: "none",
                    },
                    ".MuiTablePagination-input": {
                      marginRight: "15px",
                    },
                  }}
                />
              </Stack>
            </TableContainer>
            {/* Duplicate modal */}
            <DuplicateMapModal
              open={openModal}
              handleClose={handleCloseModal}
            />
            {/* Maps Insight Expression Modal */}
            <MapsInsightExpressionModal
              open={openExpressionModal}
              expressionHandleClose={expressionHandleClose}
              expressionTableHeadings={expressionTableHeadings}
            />
          </Paper>
        </>
      )}
    </>
  );
};

export default MapsInsight;
